//Imports
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//Actions
import { reset, manageSearchCrews } from "./redux/slice";
import {
  sendGenericExport,
  clearRedux,
  catalogLoad,
  savePreference,
} from "Redux/actions";
import {
  openModalCreateEditManageCrew,
  openModalEditManageUnitCrew,
} from "./modals/createEditManageCrew/redux/slice";
import { openModalDeleteManageCrew } from "./modals/deleteManageCrew/redux/slice";

//Controls
import {
  Container,
  IconButton,
  MenuComponent,
  Pill,
  SearchInput,
  TableButtons,
  TableView,
  useTablePagination,
} from "@gpstrackit/gtc-react-controls";
import { HasPermission } from "Core/security";

//Components
import { Grid } from "@mui/material";
import { NotificationManager } from "../../../../components/Notifications";
import Preferences from "Core/containers/Preferences";

//Helpers
import { completeColumns } from "Core/data/Helpers";

//Modals
import CreateEditManageCrew from "./modals/createEditManageCrew";
import DeleteManageCrew from "./modals/deleteManageCrew";

//Theme
import styles from "Assets/sass/core/vars.scss";
import { disableItemSelection } from "../../.globals/utils";
import VehicleCrewTab from "./components/vehicles";
import { manageSearchUnits } from "./components/vehicles/redux/slice";

const CREWS_KEY_PREFERENCE = "Manage-CrewTab-crews-list";
const UNITS_KEY_PREFERENCE = "Manage-CrewTab-units-list";
const INITIAL_SORT = { key: "firstName", order: "asc" };

const CrewTab = () => {
  //States
  const [selectedItems, setSelectedItems] = useState([]);
  const [isCrewMembers, setIsCrewMembers] = useState(true);
  const [preferencesCrews, setPreferencesCrews] = useState({});
  const [preferencesUnits, setPreferencesUnits] = useState({});
  const [loadPreferencesCrews, setLoadPreferencesCrews] = useState(true);
  const [loadColumnsCrews, setLoadColumnsCrews] = useState(false);

  //Table refs
  const crewTableRef = useRef();
  const unitsTableRef = useRef();


  //Language
  const { messages, locale } = useIntl();

  //Redux
  const { loadingCrews, crews, totalCrews, error } = useSelector(
    (state) => state.manageCrewTabRedux
  );
 
  const openCreateEditManageCrew = useSelector(
    (state) => state.manageCreateEditManageCrewRedux.open
  );
  const openDeleteManageCrew = useSelector(
    (state) => state.manageDeleteManageCrewRedux.open
  );
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const { totalUnits } = useSelector((state) => state.manageUnitsRedux);
  const authenticatedId = useSelector(
    (state) => state.securityRedux.authenticatedId
  );
  const user = useSelector((state) => state.securityRedux.user);

  // Permissions
  const editablePermissions = HasPermission(user, "", [337])

  const dispatch = useDispatch();

  // Pagination for Crew Members
  const paginationCrews = useTablePagination({
    dispatch,
    action: manageSearchCrews,
    searchFields: ["firstName", "lastName", "unit_label"],
    tableRef: crewTableRef,
    sendGenericExport,
    exportName: "crew",
  });

  // Pagination for Units Vehicles
  const paginationUnits = useTablePagination({
    dispatch,
    totalItems: totalUnits,
    action: manageSearchUnits, 
    searchFields: ["label"],
    tableRef: unitsTableRef,
    sendGenericExport,
    exportName: "vehicles",
    
  });

  if (error) {
    NotificationManager.error(
      messages["errorMessage"],
      messages["error"],
      3000,
      null,
      null,
      "warning"
    );
    dispatch(reset({ error: null }));
  };

  if (exportResult && (exportName === "crew" || exportName === "vehicles")) {
    NotificationManager.info(
      messages["exportInProcessBody"],
      messages["exportInProcessTitle"],
      4000,
      null,
      null,
      "info",
      null
    );
    dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
  };

  //Options
  const getCrewsMenuOptions = (unit) => [
    messages["edit"],
    unit ? messages["manage_crew_crews_reAssign"] : undefined,
    messages["delete"],
  ];

  const sectionsCrew = [
    {
      label: messages["manage_crew_crewMembers"],
      id: "crew",
    },
    {
      label: messages["manage_crew_units"],
      id: "vehicle",
    },
  ];

  //Handlers and utils
  const handleClickCrewMenu = (type, crew) => {
    if (type === messages["edit"]) {
      dispatch(openModalCreateEditManageCrew({ crew }));
      return;
    }
    if (type === messages["manage_crew_crews_reAssign"]) {
      dispatch(openModalCreateEditManageCrew({ crew, isAssignToUnit: true }));
      return;
    }
    if (type === messages["delete"]) {
      dispatch(openModalDeleteManageCrew({ deleteCrew: crew }));
      return;
    }
  };

  const handleDeleteCrewList = useCallback(() => {
    dispatch(openModalDeleteManageCrew({ deleteCrewList: selectedItems }));
  }, [selectedItems]);

  
  const refreshCrewTable = () => {
    paginationCrews.onSearchChange("")
    paginationUnits.onSearchChange("");
    dispatch(manageSearchCrews({}));
    dispatch(manageSearchUnits({}));
  };

  const ensureVisibleColumns = useCallback((columns, fallbackKey) => {
    if (!Array.isArray(columns) || !columns.length) return columns;

    const hasVisibleColumns = columns.some((column) => column?.show !== false);
    if (hasVisibleColumns) return columns;

    const hasFallbackColumn = columns.some((column) => column?.key === fallbackKey);

    return columns.map((column, index) => ({
      ...column,
      show: hasFallbackColumn
        ? column?.key === fallbackKey
        : index === 0,
    }));
  }, []);

  const savePreferencesCrews = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected preferences to be an array");
        return;
      }

      let updatedPreferences = { ...preferencesCrews } || {};

      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });

      setPreferencesCrews(updatedPreferences);

      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: CREWS_KEY_PREFERENCE,
          preferenceValue: updatedPreferences,
        })
      );
    },
    [preferencesCrews, authenticatedId]
  );

  const savePreferencesUnits = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected preferences to be an array");
        return;
      }

      let updatedPreferences = { ...preferencesUnits } || {};

      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });

      setPreferencesUnits(updatedPreferences);

      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: UNITS_KEY_PREFERENCE,
          preferenceValue: updatedPreferences,
        })
      );
    },
    [preferencesUnits, authenticatedId]
  );

  const currentCrewsColumns = useMemo(
    () => [
      {
        key: "firstName",
        title: messages["manage_crew_crews_name"],
        functionString: 'return row?.firstName + " " + row?.lastName || ""',
        sort: true,
        sortField: "firstName",
        show: true,
        export: true,
        exportValue: (row) => row?.firstName + " " + row?.lastName || "",
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>{row?.firstName + " " + row?.lastName}</p>
            </Grid>
            {editablePermissions &&
              <Grid item>
                <MenuComponent
                  placement="right-start"
                  options={getCrewsMenuOptions(row?.unit?.label)}
                  icon="more_vert"
                  className="showInHover"
                  onClick={handleClickCrewMenu}
                  value={row}
                />
              </Grid>
            }
          </Grid>
        ),
      },
      {
        key: "unitId",
        title: messages["manage_crew_crews_assignedTo"],
        functionString: 'return row?.unit?.label || ""',
        sort: true,
        sortField: "unitId",
        show: true,
        export: true,
        exportValue: (row) => row?.unit?.label,
        render: (row) =>
          row?.unit?.label ? (
            <Grid container alignItems="center">
              <Grid item>
                <p>{row?.unit?.label}</p>
              </Grid>
            </Grid>
          ) : (
            <Fragment>
              {editablePermissions &&
                <a
                  className="link"
                  onClick={() =>
                    dispatch(
                      openModalCreateEditManageCrew({
                        crew: row,
                        isAssignToUnit: true,
                      })
                    )
                  }
                >
                  {messages["assign"]}
                </a>
              }
            </Fragment>
          ),
      },
    ],
    [locale]
  );

  useEffect(() => {
    paginationCrews.setColumns(currentCrewsColumns);
    crewTableRef?.current?.setPage(1);
    crewTableRef?.current?.setSelectedItems([]);
  }, [ currentCrewsColumns]);

  useEffect(() => {
    if (!isCrewMembers) return;
    setTimeout(() => {
      let sort = preferencesCrews?.sort || INITIAL_SORT; // * Initial sort
      crewTableRef.current?.setSort?.(sort);
    }, 100);
  }, [isCrewMembers, preferencesCrews]);

 
  useEffect(() => {
    if (preferencesCrews?.columns?.length && !loadColumnsCrews) {
      const preferenceMap = new Map(
        preferencesCrews.columns.map((col) => [col.key, col])
      );

      // Reorder columns based on preferences (keeping `show`)
      const reorderedColumns = preferencesCrews.columns
        .map((pref) => {
          const col = currentCrewsColumns.find((c) => c.key === pref.key);
          return col ? { ...col, show: pref.show } : null;
        })
        .filter(Boolean);

      // Add new columns (not listed in preferences), with `show: true` by default
      const newColumns = currentCrewsColumns
        .filter((col) => !preferenceMap.has(col.key))
        .map((col) => ({ ...col, show: true }));

      const finalColumns = [...reorderedColumns, ...newColumns];
      paginationCrews.setColumns(finalColumns);
      setPreferencesCrews({ ...preferencesCrews });
      setLoadColumnsCrews(true);
    }
  }, [paginationCrews.columns, loadColumnsCrews, preferencesCrews]);


  useEffect(() => {
    if (paginationCrews.columns.length > 0) {
      setPreferencesCrews((currentPreferences) => ({
        ...currentPreferences,
        columns: paginationCrews.columns,
      }));
      setLoadColumnsCrews(false);
    }

  }, [locale]);


  return (
    <Fragment>
      <Container maxWidth={false}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item xs={6} sm={2}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
            >
              <Grid item style={{ display: "flex" }}>
                {editablePermissions &&
                  <IconButton
                    tooltip={messages["add"]}
                    placement="bottom"
                    variant="secondary"
                    icon="add_box"
                    size="small"
                    disabled={loadingCrews}
                    onClick={() => dispatch(openModalCreateEditManageCrew())}
                  />
                }
                {isCrewMembers && editablePermissions ? (
                  <IconButton
                    tooltip={messages["delete"]}
                    placement="bottom"
                    variant="secondary"
                    icon="delete"
                    size="small"
                    onClick={() => handleDeleteCrewList()}
                    style={{ opacity: !selectedItems.length && "0.6" }}
                    isDisabled={!selectedItems.length}
                  />
                ) : (
                  <></>
                )}
                <TableButtons
                  messages={messages}
                  {...(isCrewMembers ? paginationCrews : paginationUnits)}
                  buttons={["export", "orderColumn"]}
                  onExport={(type) => {
                    if (!isCrewMembers) {
                      paginationUnits.onExport(type);
                      return;
                    }
                    paginationCrews.onExport(type);
                  }}
                  onChangeColumns={(columns) => {
                    if (isCrewMembers) {
                      const safeColumns = ensureVisibleColumns(columns, "firstName");
                      savePreferencesCrews([
                        { key: "columns", value: safeColumns },
                      ]);
                      paginationCrews.setColumns(safeColumns);
                      return;
                    }
                    const safeColumns = ensureVisibleColumns(columns, "label");
                    savePreferencesUnits([{ key: "columns", value: safeColumns }]);
                    paginationUnits.setColumns(safeColumns);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            direction={"row"}
            item
            style={{ textAlign: "center", display: "flex" }}
          >
            {sectionsCrew.map((filter, index) => {
              let variant = "outlined";
              let color = undefined;
              if (filter.id === "crew" && isCrewMembers) {
                variant = "filled";
                color = styles.blue_gtc;
              } else if (filter.id === "vehicle" && !isCrewMembers) {
                variant = "filled";
                color = styles.blue_gtc;
              }

              return (
                <Pill
                  key={index}
                  textField={"name"}
                  item={{ ...filter, color }}
                  variant={variant}
                  onClick={() => {
                    refreshCrewTable()
                    if (filter.id === "crew") {
                      setIsCrewMembers(true);
                    } else if (filter.id === "vehicle") {
                      setIsCrewMembers(false);
                    }
                  }}
                  style={{ border: "0" }}
                  label="pill-label-text"
                  colorField="default"
                  colorSelection="005bf8"
                />
              );
            })}
          </Grid>
          <Grid item xs={12} sm={4} className="mb-10 text-right">
            <SearchInput
              placeholder={messages["manage_crews_search_name_assignedTo"]}
              searchText={
                isCrewMembers
                  ? paginationCrews.searchText
                  : paginationUnits.searchText
              }
              onSearchText={
                isCrewMembers
                  ? paginationCrews.onSearchChange
                  : paginationUnits.onSearchChange
              }
            />
          </Grid>
        </Grid>
        {isCrewMembers ? (
          <Preferences
            preferencesKey={CREWS_KEY_PREFERENCE}
            onInitialPreferences={(data) => {
              if (!loadPreferencesCrews) return;
              setTimeout(() => {
                let sort = data?.sort || INITIAL_SORT; // * Initial sort
                crewTableRef.current?.setSort?.(sort);
                paginationCrews.onChangeSort(sort);
              }, 100);
              setPreferencesCrews(data);
              setLoadPreferencesCrews(false);
            }}
          >
            <TableView
              keyColumn="id"
              loading={loadingCrews || loadPreferencesCrews}
              topheight={235}
              data={crews}
              total={totalCrews}
              disableItemSelection={disableItemSelection(preferencesCrews)}
              exportOrientation="landscape"
              onSelectedItems={(items) => setSelectedItems(items)}
              {...paginationCrews}
              onChangeSort={(newSort) => {
                savePreferencesCrews([{ key: "sort", value: newSort }]);
                paginationCrews.onChangeSort(newSort);
              }}
              onChangeColumns={(columns) => {
                const currentColumns = paginationCrews?.columns || [];
                const finalsColumns = completeColumns(currentColumns, columns);
                const safeColumns = ensureVisibleColumns(finalsColumns, "firstName");
                savePreferencesCrews([
                  { key: "columns", value: safeColumns },
                ]);
                paginationCrews.onChangeColumns(safeColumns);
              }}
            />
          </Preferences>
        ) : (
          <VehicleCrewTab  paginationUnits={paginationUnits} />
        )}
      </Container>
      {openCreateEditManageCrew && (
        <CreateEditManageCrew refresh={refreshCrewTable} paginationUnits={paginationUnits} />
      )}
      {openDeleteManageCrew && <DeleteManageCrew refresh={refreshCrewTable} />}
    </Fragment>
  );
};

export default CrewTab;
